import React, { useEffect, useMemo, useRef, useState } from "react";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FINANCE_V2_HOSTNAME,
  INTRANETHOSTNAME,
  INVOICE_TRACKING_HOSTNAME,
} from "../../../../common-utilities/utils/end-points";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { createPayloadMapper, mapPOResponseToFormDetails } from "./helper";
import { isStoreRole } from "../../../utils/common-utils";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import {
  Autocomplete,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import "./index.scss";
import { AddCircle, ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import AddItemModal from "./add-item-modal";
import {
  convertToCamelCaseFromUnderScore,
  formatDate,
} from "../../../../common-utilities/utils";
import { toast } from "react-toastify";
import FileUpload from "../../../../common-utilities/core/file-uploader";
import { DATE_FORMATS } from "../../../../common-utilities/utils/constants";

import {
  getPOAmount,
  getPOLineItemAmount,
  getAdvanceAmount,
  createPOavailableBudgetAmount,
  getPOAmountWithoutTax,
  getExtraChargesTotal,
  PO_ITEM_MODEL,
  normalizeLocationId,
  getAllocationForItem,
  computeAllocationAvailable,
} from "../helper";
import { getCurrencyList } from "../../master-data/foreign-currency/apis";
import CancelPo from "../common-components/cancel-po-button";
import ExtraChargesSection, {
  hasIncompleteChargeRow,
} from "../common-components/extra-charges-section";
import {
  fetchRateContractStatus,
  getRateContractRequiredItemIds,
} from "../rate-contract-utils";
import { getOfflineData } from "../../../utils/offline-services";
import {
  getUploadAttachmentQueryParams,
  uploadAttachmentTypes,
} from "../../../utils/attachments";
import { setProperty } from "../../../../common-utilities/utils/misc";

// A linked PR's `site` is returned as a populated Location object ({ id, name, ... })
// for scopes keyed to a single site (SINGLE_STORE, HEAD_OFFICE/SELF), while
// `selectedSites` (multi-store scopes) is always a plain array of numeric Location ids.
// Normalize `site` down to its plain id before comparing against customSites (also
// keyed by Location id) -- otherwise the comparison silently never matches.
const resolvePRSiteId = (site) =>
  site && typeof site === "object" ? site.id ?? site._id : site;

const EditPO = ({}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formDetails, setFormDetails] = useState({});
  const [poItems, setPoItems] = useState({});
  const [vendors, setVendors] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [autocompleteInputValues, setAutocompleteInputValues] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [customSites, setCustomSites] = useState([]);
  const [selectedLineItemIds, setSelectedLineItemIds] = useState([]);
  const [loadedPO, setLoadedPO] = useState(false);
  const { vendor = {}, budgetDetails = {} } = formDetails;
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [poDetails, setPoDetails] = useState({});
  const [prDetails, setPrDetails] = useState(null);
  const [prItemRemainingMap, setPrItemRemainingMap] = useState({});
  // Fallback lookup (lineItemId::itemId -> balance), for PO items whose prItemId can't be
  // matched in prItemRemainingMap -- e.g. a PO saved before the prItemId-preserving fixes
  // above existed. lineItemId+itemId is stable even when the real prItemId was lost.
  const [prItemRemainingByComposite, setPrItemRemainingByComposite] = useState({});
  const isPRLinked = !!poDetails.purchaseRequestId;
  const isSingleStorePRLinked = isPRLinked && prDetails?.prScope === "SINGLE_STORE";
  const isSelfPRLinked =
    isPRLinked &&
    prDetails?.prScope === "HEAD_OFFICE" &&
    prDetails?.storeSelectionType === "SELF";
  // Both scopes resolve to exactly one valid delivery store for the PR, so it's
  // prefilled and locked rather than left as a free choice among filtered options.
  const isDeliveryLocationLocked = isSingleStorePRLinked || isSelfPRLinked;
  const user = getOfflineData("user");
  const [config, setConfig] = useState({
    isItemDeleted: false,
  });
  const [extraCharges, setExtraCharges] = useState([]);
  // Keyed by itemId -> { active, rate, taxPercentage }. Covers every rateContractRequired
  // item across all rows regardless of whether the PO itself is PR-linked or independent.
  const [rateContractStatusMap, setRateContractStatusMap] = useState({});
  const [rateContractRevalidating, setRateContractRevalidating] = useState(false);

  const { lineItems = [], siteCode, siteName = "" } = budgetDetails;
  const poSubtotalBeforeTax = Number(getPOAmountWithoutTax(poItems)) || 0;

  // PR-linked POs display the currently selected Delivery Location's own site --
  // budgetDetails.site is only ever an arbitrary pooled STORE's budget for HEAD_OFFICE
  // PRs, not the store the PO is actually for. For multi-store HO PRs this stays blank
  // until the user picks a Delivery Location, then updates immediately to that store.
  // Independent (non-PR) POs keep showing the saved PO's own budget site, unchanged.
  const displaySiteName = isPRLinked
    ? formDetails.deliveryLocation?.name || ""
    : siteName;
  const displaySiteCode = isPRLinked
    ? formDetails.deliveryLocation?.code || ""
    : siteCode;

  useEffect(() => {
    loadData();
    getCurrencyList(setCurrencyOptions);
  }, []);

  useEffect(() => {
    if (
      departmentList.length > 0 &&
      vendors.length > 0 &&
      !formDetails.poType &&
      !loadedPO
    ) {
      setLoadedPO(true);
      invokeApi(
        HTTP_METHODS.GET,
        `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PO}`,
        null,
        {
          page: 1,
          limit: 10,
          _id: id,
        }
      )
        .then((response) => {
          if (response.message) {
          } else if (response.results.length > 0) {
            const {
              mappedResponse,
              mappedAutoCompleteInputValues,
              poItems = {},
            } = mapPOResponseToFormDetails(
              response.results[0],
              departmentList,
              vendors
            );

            setFormDetails(mappedResponse);
            setAutocompleteInputValues(mappedAutoCompleteInputValues);
            setSelectedLineItemIds(mappedResponse.selectedLineItemIds);
            setPoItems(poItems);
            setPoDetails(response.results[0]);
            setExtraCharges(
              Array.isArray(response.results[0]?.extraCharges)
                ? response.results[0].extraCharges
                : []
            );
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [departmentList, vendors]);

  // fetch the linked PR, if any, so its store list can filter the Delivery Location dropdown
  useEffect(() => {
    if (!poDetails.purchaseRequestId) return;
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}/${poDetails.purchaseRequestId}`
    )
      .then((data) => {
        if (!data.message) setPrDetails(data);
      })
      .catch(() => {
        toast.error(
          "Could not load the linked Purchase Requisition's store details. Delivery Location options will stay restricted until this succeeds."
        );
      });
  }, [poDetails.purchaseRequestId]);

  // Build a PR item id -> balance quantity lookup once the linked PR resolves, scoped to
  // whichever store is currently selected as Delivery Location (falls back to the PR's own
  // site for SINGLE_STORE/HEAD_OFFICE-SELF, which resolves their one allocation immediately).
  // Re-derives whenever the Delivery Location changes so a multi-store PR's balance always
  // reflects the currently selected store's own allocation, never a pooled/PR-wide number.
  useEffect(() => {
    if (!prDetails) return;
    const storeId =
      normalizeLocationId(formDetails.deliveryLocation) ?? normalizeLocationId(prDetails.site);
    const map = {};
    const compositeMap = {};
    (prDetails.items || []).forEach((prItem) => {
      const allocation = getAllocationForItem(prItem, storeId);
      const remaining = allocation ? computeAllocationAvailable(allocation) : 0;
      map[prItem.id] = remaining;
      const prLineItemId = prItem.lineItem?.id ?? prItem.lineItem;
      const prMasterItemId = prItem.item?.id ?? prItem.item?._id ?? prItem.item;
      if (prLineItemId != null && prMasterItemId != null) {
        compositeMap[`${prLineItemId}::${prMasterItemId}`] = remaining;
      }
    });
    setPrItemRemainingMap(map);
    setPrItemRemainingByComposite(compositeMap);
  }, [prDetails, formDetails.deliveryLocation]);

  // A PO row's balance on the linked PR: by prItemId, else by lineItemId::itemId (same
  // fallback the backend's resolvePOItemsAgainstPR uses when prItemId is stale).
  const getPrRemainingForPoItem = (poItem) => {
    const byPrItemId =
      poItem.prItemId != null ? prItemRemainingMap[poItem.prItemId] : undefined;
    return byPrItemId !== undefined
      ? byPrItemId
      : prItemRemainingByComposite[`${poItem.lineItemId}::${poItem.itemId}`];
  };

  // PR-linked: the PR's own estimate for each row's quantity, per line item. The PR already
  // reserved it out of availableAmount, so it's credited back before comparing -- mirrors the
  // backend's budgetOffset (computeActualAndEstimatedByLineItem) on update/resend, and
  // create-po's prBudgetOffsetByLineItem.
  const prBudgetOffsetByLineItem = useMemo(() => {
    const out = {};
    if (!prDetails) return out;
    const byId = {};
    const byComposite = {};
    (prDetails.items || []).forEach((prItem) => {
      byId[prItem.id] = prItem;
      const prLineItemId = prItem.lineItem?.id ?? prItem.lineItem;
      const prMasterItemId = prItem.item?.id ?? prItem.item?._id ?? prItem.item;
      byComposite[`${prLineItemId}::${prMasterItemId}`] = prItem;
    });
    Object.entries(poItems || {}).forEach(([liId, items]) => {
      out[liId] = (items || []).reduce((sum, poItem) => {
        const prItem =
          (poItem.prItemId != null && byId[poItem.prItemId]) ||
          byComposite[`${poItem.lineItemId ?? liId}::${poItem.itemId}`];
        if (!prItem) return sum;
        return (
          sum +
          (Number(poItem.quantity) || 0) *
            (Number(prItem.estimatedRate) || 0) *
            (1 + (Number(prItem.taxRate) || 0) / 100)
        );
      }, 0);
    });
    return out;
  }, [prDetails, poItems]);

  const getEffectiveAvailableAmount = (lineItem) =>
    (Number(lineItem?.availableAmount) || 0) +
    (prBudgetOffsetByLineItem[lineItem?.id] || 0);

  // single-site PR path (SINGLE_STORE, or HEAD_OFFICE/SELF): auto-fill and lock Delivery
  // Location to the PR's own site. prDetails.site is the PR's populated Location ref
  // ({ id, name, ... }) here, not a plain id -- resolvePRSiteId() normalizes it so this
  // never falls back to comparing an object against a string.
  useEffect(() => {
    if (!isDeliveryLocationLocked) return;
    if (formDetails.deliveryLocation) return;
    if (!customSites.length || !prDetails?.site) return;
    const prSiteId = resolvePRSiteId(prDetails.site);
    const matched = customSites.find((s) => String(s.id) === String(prSiteId));
    if (!matched) return;
    setFormDetails((prev) => ({ ...prev, deliveryLocation: matched }));
    setAutocompleteInputValues((prev) => ({
      ...prev,
      deliveryLocation: matched.restaurantName,
    }));
  }, [isDeliveryLocationLocked, customSites, prDetails, formDetails.deliveryLocation]);

  // PR-linked: Billing Location defaults to the PR's requesting HO/site location,
  // resolved from prDetails.site -- independent of whichever store is picked as the
  // multi-store PR's Delivery Location. Only fills when there's no id (mapPOResponseToFormDetails
  // seeds billingLocation as at least `{}`), so the saved/user-picked location is kept.
  // Applied at most once -- otherwise clearing the field to search (which unsets the id)
  // immediately re-filled it with the PR's location.
  const billingDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (billingDefaultAppliedRef.current) return;
    if (!isPRLinked) return;
    if (!customSites.length || !prDetails?.site) return;
    if (formDetails.billingLocation?.id) {
      billingDefaultAppliedRef.current = true;
      return;
    }
    const prSiteId = resolvePRSiteId(prDetails.site);
    const matched = customSites.find((s) => String(s.id) === String(prSiteId));
    if (!matched) return;
    billingDefaultAppliedRef.current = true;
    setFormDetails((prev) => ({ ...prev, billingLocation: matched }));
    setAutocompleteInputValues((prev) => ({
      ...prev,
      billingLocation: matched.restaurantName,
    }));
  }, [isPRLinked, customSites, prDetails, formDetails.billingLocation]);

  // multi-store PR path (non-locked): if the current selection falls outside the PR's
  // valid store list -- e.g. it was picked before prDetails narrowed the options, or the
  // PR data was still loading -- clear it instead of letting a stale, out-of-scope
  // delivery location reach the payload.
  useEffect(() => {
    if (!poDetails.purchaseRequestId || isDeliveryLocationLocked) return;
    if (!prDetails) return;
    if (!formDetails.deliveryLocation) return;
    const validIds = getDeliveryAddressList().map((s) => String(s.id));
    if (validIds.includes(String(formDetails.deliveryLocation.id))) return;
    setFormDetails((prev) => ({ ...prev, deliveryLocation: null }));
    setAutocompleteInputValues((prev) => ({ ...prev, deliveryLocation: "" }));
  }, [
    poDetails.purchaseRequestId,
    isDeliveryLocationLocked,
    prDetails,
    customSites,
    formDetails.deliveryLocation,
  ]);

  // PR-linked: reset each row's quantity to the newly selected store's own balance whenever
  // the user actually switches Delivery Location -- allocations are per-store, so a quantity
  // valid for the previous store may not even exist for the new one. Guards against firing on
  // the initial load (the saved PO's own delivery location resolving for the first time),
  // which must leave the saved PO's quantities untouched.
  const lastDeliveryLocationIdRef = useRef(undefined);
  useEffect(() => {
    if (!isPRLinked) return;
    const currentId = formDetails.deliveryLocation?.id;
    if (currentId == null) return;
    const previousId = lastDeliveryLocationIdRef.current;
    lastDeliveryLocationIdRef.current = currentId;
    if (previousId == null || String(previousId) === String(currentId)) return;
    if (Object.keys(prItemRemainingMap).length === 0) return;
    setPoItems((prev) => {
      const next = {};
      Object.entries(prev).forEach(([liId, items]) => {
        next[liId] = items.map((item) => {
          // Resolve via the same prItemId -> lineItemId::itemId fallback as the render path --
          // a stale prItemId alone used to zero the row, which then silently dropped it from
          // the update payload (createPayloadMapper skips quantity <= 0).
          const balance = getPrRemainingForPoItem(item);
          if (balance === undefined) return item;
          return { ...item, quantity: balance, remainingQuantity: balance };
        });
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPRLinked, formDetails.deliveryLocation, prItemRemainingMap, prItemRemainingByComposite]);

  // Revalidate every rateContractRequired row against the current vendor -- fires once the
  // saved PO's vendor loads, and again whenever the user changes the vendor. Rows that are
  // already associated with the PO always stay visible; rows lacking an active contract are
  // simply flagged (rateContractMissing, computed at render time) and disabled, never removed.
  useEffect(() => {
    const vendorCode = formDetails.vendor?.code;
    if (!vendorCode) return;
    const requiredItemIds = getRateContractRequiredItemIds(poItems);
    if (requiredItemIds.length === 0) {
      setRateContractStatusMap({});
      return;
    }
    setRateContractRevalidating(true);
    Promise.all(
      requiredItemIds.map((itemId) =>
        fetchRateContractStatus(vendorCode, itemId, formDetails.poDate).then((status) => ({
          itemId,
          status,
        }))
      )
    ).then((results) => {
      const map = {};
      results.forEach(({ itemId, status }) => {
        map[itemId] = status;
      });
      setRateContractStatusMap(map);
      // Populate rate/tax from the *current* contract for rows that are still/now valid.
      const updated = {};
      Object.keys(poItems).forEach((liId) => {
        updated[liId] = (poItems[liId] || []).map((poItem) => {
          const status = poItem.itemId ? map[poItem.itemId] : undefined;
          if (!status?.active) return poItem;
          return { ...poItem, rate: status.rate, taxPercentage: status.taxPercentage };
        });
      });
      setPoItems(updated);
      setRateContractRevalidating(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDetails.vendor?.code]);

  const loadData = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${FINANCE_REST_URLS.DEPARTMENTS}`,
      null,
      {
        page: 1,
        limit: 1000,
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setDepartmentList(response || []);
        }
      })
      .catch((err) => {});

    invokeApi(
      HTTP_METHODS.GET,
      `${INVOICE_TRACKING_HOSTNAME}${FINANCE_REST_URLS.VENDOR_LIST}`,
      null,
      {
        page: 1,
        limit: 1000,
        additionalFields: "paymentTerms",
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setVendors(response || []);
        }
      })
      .catch((err) => {});

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.LOCATIONS}`,
      null,
      {
        page: 1,
        // limit: 1000,
        limit: 1200,
      }
    )
      .then((response) => {
        if (response.message) {
        } else if (response && response.results.length > 0) {
          setCustomSites(
            response.results.map((r) => {
              return {
                id: r.id,
                name: r.name,
                code: r.code,
                restaurantName: `${r.name} | ${r.address}`,
              };
            }) || []
          );
        }
      })
      .catch((err) => {});

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.COMPANY}`,
      null,
      {
        page: 1,
        limit: 1000,
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setCompanyList(response.results || []);
        }
      })
      .catch((err) => {});
  };

  // Resolves true only when the PUT succeeded, so Send/Resend for Approval can save the
  // on-screen edits first and never (re)send the stale persisted PO instead.
  const onSave = ({ silent = false } = {}) => {
    if (!poDetails.purchaseRequestId) {
      const prRequiredItem = Object.values(poItems || {})
        .flat()
        .find((poItem) => poItem?.itemId && poItem?.item?.prRequired);
      if (prRequiredItem) {
        toast.error(
          "PR is required for this item. Please create a PR before creating a PO."
        );
        return Promise.resolve(false);
      }
    }

    if (hasIncompleteChargeRow(extraCharges)) {
      toast.error(
        "Please complete or remove the incomplete extra charge row before saving."
      );
      return Promise.resolve(false);
    }

    if (rateContractRevalidating) {
      toast.warning("Still checking rate contracts for the selected vendor. Please wait.", {
        autoClose: 2500,
      });
      return Promise.resolve(false);
    }

    if (isBudgetExceeded()) {
      toast.error(
        "The purchase order amount exceeds available budget for one or more line items. Please resolve before saving.",
        { autoClose: 3500 }
      );
      return Promise.resolve(false);
    }

    let payload = createPayloadMapper(
      formDetails,
      poItems,
      budgetDetails,
      formDetails.poType,
      attachments,
      isPRLinked,
      extraCharges
    );
    return invokeApi(
      HTTP_METHODS.PUT,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PO}/${id}`,
      payload
    )
      .then((response) => {
        if (response.code) {
          toast.error(response.message || "Failed to update purchase order ");
          return false;
        }
        if (!silent) toast.info("PO Details updated successfully");
        return true;
      })
      .catch((err) => false);
  };

  const inputChange = (event) => {
    setFormDetails({ ...formDetails, [event.target.name]: event.target.value });
  };

  const onDropDownChange = (key, value) => {
    let additionalDetails = {};
    switch (key) {
      case "vendor":
        additionalDetails.paymentTerms = `${value?.paymentTerms || ""}`;
        break;
      default:
        break;
    }

    setFormDetails({ ...formDetails, [key]: value, ...additionalDetails });
  };

  const updateItem = (item, index) => {
    poItems[item.lineItemId][index] = {
      ...poItems[item.lineItemId][index],
      ...item,
    };
    setPoItems({ ...poItems });
  };

  const onDeleteRow = (id, index) => {
    setProperty("isItemDeleted", true, setConfig);
    poItems[id].splice(index, 1);
    setPoItems({ ...poItems });
    setTimeout(() => {
      setProperty("isItemDeleted", false, setConfig);
    }, 200);
  };

  const onRowAdd = (lineItem) => {
    let items = poItems[lineItem.id] || [];
    poItems[lineItem.id] = [...items, PO_ITEM_MODEL];
    setPoItems({ ...poItems });
  };

  const getAddressList = () => {
    if (customSites.length > 0) {
      return customSites;
    } else {
      return [];
    }
  };

  const getDeliveryAddressList = () => {
    const addressList = getAddressList();
    if (!poDetails.purchaseRequestId) return addressList;
    // PR-linked but the PR's own store data hasn't loaded yet -- fail closed (no options)
    // rather than briefly offering every location in the system.
    if (!prDetails) return [];

    const prStoreIds = (
      prDetails.selectedSites?.length > 0
        ? prDetails.selectedSites
        : [resolvePRSiteId(prDetails.site)]
    )
      .filter(Boolean)
      .map(String);

    return addressList.filter((s) => prStoreIds.includes(String(s.id)));
  };

  const onDelete = (index) => {
    attachments.splice(index, 1);
    setAttachments([...attachments]);
  };

  const isRateContractBlocking = () => rateContractRevalidating;

  // Mirrors create-po's isAmtValid check: the PO's actual (contract-rate-adjusted)
  // amount for each budget line item must not exceed that line item's real available
  // budget. PR-linked POs credit back the PR's own estimate first (prBudgetOffsetByLineItem);
  // independent POs have no offset, so their check is unchanged.
  const isBudgetExceeded = () => {
    let exceeded = false;
    lineItems?.forEach((lineItem) => {
      if (
        lineItem?.status === "approved" &&
        parseFloat(getPOLineItemAmount(poItems[lineItem.id] || [], "taxPercentage")) >
          getEffectiveAvailableAmount(lineItem)
      ) {
        exceeded = true;
      }
    });
    return exceeded;
  };

  const isActionBlocked = () => isRateContractBlocking() || isBudgetExceeded();

  const sendForApproval = () => {
    if (isRateContractBlocking()) {
      toast.warning("Still checking rate contracts for the selected vendor. Please wait.", {
        autoClose: 2500,
      });
      return;
    }
    if (isBudgetExceeded()) {
      toast.error(
        "The purchase order amount exceeds available budget for one or more line items. Please resolve before sending for approval.",
        { autoClose: 3500 }
      );
      return;
    }
    onSave({ silent: true })
      .then((saved) =>
        saved
          ? invokeApi(
              HTTP_METHODS.POST,
              `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.SEND_FOR_APPROVAL}${id}`,
              null
            )
          : null
      )
      .then((response) => {
        if (!response) return;
        if (response.code) {
          toast.error(response.message || "Failed to send for approval");
        } else {
          toast.info("PO sent for approval");
          navigate(`/finance-v2/purchase-order/${id}`);
        }
      })
      .catch((err) => {});
  };

  const resendForApproval = () => {
    if (isRateContractBlocking()) {
      toast.warning("Still checking rate contracts for the selected vendor. Please wait.", {
        autoClose: 2500,
      });
      return;
    }
    if (isBudgetExceeded()) {
      toast.error(
        "The purchase order amount exceeds available budget for one or more line items. Please resolve before resending for approval.",
        { autoClose: 3500 }
      );
      return;
    }
    const { approvalId = {} } = formDetails;
    onSave({ silent: true })
      .then((saved) =>
        saved
          ? invokeApi(
              HTTP_METHODS.POST,
              `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.RESEND_FOR_APPROVAL}${approvalId.id}`,
              null
            )
          : null
      )
      .then((response) => {
        if (!response) return;
        if (response.code) {
          toast.error(response.message || "Failed to send for approval");
        } else {
          toast.info("PO sent for approval");
          navigate(`/finance-v2/purchase-order/${id}`);
        }
      })
      .catch((err) => {});
  };

  return (
    <div className="finance-v2-edit-po-container">
      <div className="po-edit-header">
        <div className="left-container">
          <div className="back-title">
            <IconButton
              onClick={() => {
                navigate(-1);
                // navigate("/finance-v2?index=1");
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography className="title">
              Edit PO {formDetails.poNumber}
            </Typography>
          </div>
          <div className="info-section">
            <div className="info-box">
              <span>Site Name </span>
              <span>{displaySiteName || ""}</span>
            </div>
            <div className="info-box">
              <span>Site Code </span>
              <span>{displaySiteCode || ""}</span>
            </div>
            <div className="info-box">
              <span>PO Type </span>
              <span>
                {convertToCamelCaseFromUnderScore(formDetails.poType)}
              </span>
            </div>
            <div className="info-box">
              <span>PO Amount </span>
              <span>
                {(
                  Number(getPOAmount(poItems)) +
                  getExtraChargesTotal(extraCharges, poSubtotalBeforeTax)
                ).toFixed(2)}
              </span>
            </div>
            <div className="info-box">
              <span>PO Date </span>
              <span>
                {formatDate(formDetails.poDate, DATE_FORMATS["DD-MM-YYYY"])}
              </span>
            </div>
            <div className="info-box">
              <span>Created By</span>
              <span>{formDetails.creatorName || "-"}</span>
            </div>
            <div className="info-box">
              <span>Available Budget Amt </span>
              <span>
                {createPOavailableBudgetAmount(lineItems, selectedLineItemIds)}
              </span>
            </div>
          </div>
        </div>

        <div className="right-container">
          <CancelPo
            poDetails={poDetails}
            setPODetails={setPoDetails}
            navigateToDraft={true}
          />
          {parseInt(user?.id || user?._id) === poDetails?.createdBy && (
            <>
              <Button variant="outlined" onClick={() => onSave()} disabled={isActionBlocked()}>
                Update PO
              </Button>
              {["draft"].includes(poDetails.status) && (
                <Button variant="outlined" onClick={sendForApproval} disabled={isActionBlocked()}>
                  Send for Approval
                </Button>
              )}
              {["reconsider"].includes(poDetails.status) && (
                <Button variant="outlined" onClick={resendForApproval} disabled={isActionBlocked()}>
                  Resend for Approval
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="form-container">
        <div className="form-row">
          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("company", val);
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Company" />
            )}
            inputValue={autocompleteInputValues.company || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues({
                  ...autocompleteInputValues,
                  company: newInputValue,
                });
              }
            }}
            getOptionLabel={(op) => op.name || ""}
            options={companyList}
          />

          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("vendor", val);
            }}
            getOptionLabel={(op) => `${op.code} - ${op.name}`}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Vendor" />
            )}
            value={formDetails.vendor || ""}
            inputValue={autocompleteInputValues.vendor || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues({
                  ...autocompleteInputValues,
                  vendor: newInputValue,
                });
              }
            }}
            options={vendors}
          />
          <TextField
            value={vendor?.gst || ""}
            disabled
            size="small"
            label="Vendor GST No"
            fullWidth
          />
          <TextField
            value={vendor?.address || ""}
            disabled
            size="small"
            label="Vendor Address"
            fullWidth
          />
          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("agreement", val);
            }}
            value={formDetails.agreement || ""}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Agreement" />
            )}
            options={["Yes", "No"]}
          />
        </div>
        <div className="form-row">
          <TextField
            value={formDetails.remarks || ""}
            onChange={inputChange}
            size="small"
            name="remarks"
            label="Agreement Remarks"
            fullWidth
          />

          <Autocomplete
            disabled={isDeliveryLocationLocked}
            onChange={(e, val) => {
              onDropDownChange("deliveryLocation", val);
            }}
            value={formDetails.deliveryLocation || ""}
            inputValue={autocompleteInputValues.deliveryLocation || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues({
                  ...autocompleteInputValues,
                  deliveryLocation: newInputValue,
                });
              }
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Delivery Location" />
            )}
            getOptionLabel={(op) => op.restaurantName || ""}
            options={getDeliveryAddressList()}
          />
          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("billingLocation", val);
            }}
            value={formDetails.billingLocation || ""}
            inputValue={autocompleteInputValues.billingLocation || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues({
                  ...autocompleteInputValues,
                  billingLocation: newInputValue,
                });
              }
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Billing Location" />
            )}
            getOptionLabel={(op) => op.restaurantName || ""}
            options={getAddressList()}
          />
          <TextField
            value={formDetails.paymentTerms || ""}
            onChange={inputChange}
            size="small"
            name="paymentTerms"
            label="Payment terms(Master vendor)"
            fullWidth
          />
          {isStoreRole() ? (
            <TextField size="small" label="Department" value="Store" disabled />
          ) : (
            <Autocomplete
              onChange={(e, val) => {
                onDropDownChange("department", val);
              }}
              disabled
              getOptionLabel={(op) => op.name}
              value={formDetails.department || ""}
              inputValue={
                (formDetails.department && formDetails.department.name) || ""
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Department" />
              )}
              options={departmentList}
            />
          )}
        </div>
        <div className="form-row">
          <TextField
            value={formDetails.advancePercent || ""}
            onChange={inputChange}
            size="small"
            type="number"
            name="advancePercent"
            label="Advance Percent*"
            fullWidth
          />
          <TextField
            value={
              getAdvanceAmount(
                formDetails.advancePercent || 0,
                getPOAmountWithoutTax(poItems)
              ) || ""
            }
            onChange={inputChange}
            size="small"
            type="number"
            name="advanceAmount"
            label="Advance Amount"
            disabled
            fullWidth
          />
          <TextField
            value={formDetails.termsAndConditions || ""}
            onChange={inputChange}
            size="small"
            name="termsAndConditions"
            label="Terms and conditions"
            fullWidth
          />
          <Autocomplete
            onChange={(e, val) => {
              onDropDownChange("currency", val);
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Currency" />
            )}
            inputValue={autocompleteInputValues.currency || ""}
            onInputChange={(event, newInputValue) => {
              if (event) {
                setAutocompleteInputValues({
                  ...autocompleteInputValues,
                  currency: newInputValue,
                });
              }
            }}
            options={currencyOptions}
          />
          <TextField
            value={formDetails.forex || ""}
            onChange={inputChange}
            type="number"
            size="small"
            name="forex"
            label="Forex Rate"
            fullWidth
          />
        </div>
      </div>
      {vendor && vendor.code ? (
        <>
          <div className="po-additional-attachment-container">
            <FileUpload
              uploadText="Additional Documents"
              clsName="add-additional-doc"
              id="additional-documents"
              url={`${FINANCE_V2_HOSTNAME}${
                FINANCE_REST_URLS.UPLOAD_DOCUMENT
              }${getUploadAttachmentQueryParams(
                {
                  id: poDetails.id,
                  poNumber: poDetails.poNumber,
                  attachmentKey: "documentLinks",
                  status: poDetails.status,
                },
                uploadAttachmentTypes.poUpdate
              )}`}
              callBack={(response) => {
                const { data } = response;
                setAttachments([...attachments, data]);
              }}
              formDataKey="file"
            />
            {attachments.map((attachment, index) => {
              return (
                <Chip
                  label={attachment.originalName}
                  onDelete={() => onDelete(index)}
                  onClick={() => window.open(attachment?.docUrl, "_blank")}
                />
              );
            })}
          </div>
          <div className="finance-v2-po-add-items-container">
            <div className="added-po-items-list">
              {lineItems.map((lineItem) => {
                if (!selectedLineItemIds.includes(lineItem.id)) {
                  return;
                }
                return (
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className="po-accordion-summary"
                    >
                      <div className="accordion-header">
                        <div className="left-container">
                          <Typography>
                            {lineItem.name} (
                            {(poItems[lineItem.id] || []).length})
                          </Typography>

                          {!isPRLinked && formDetails.vendor && formDetails.vendor.id && (
                            <IconButton
                              disabled={
                                lineItem.availableAmount <= 0 ||
                                !(
                                  (formDetails.vendor &&
                                    formDetails.vendor.id) ||
                                  ""
                                )
                              }
                              onClick={(event) => {
                                onRowAdd(lineItem);
                                event.stopPropagation();
                              }}
                            >
                              <AddCircle color="primary" />
                            </IconButton>
                          )}
                        </div>
                        <div className="right-container">
                          <span>
                            Available Amount :{" "}
                            {Math.round(getEffectiveAvailableAmount(lineItem)).toFixed(2)}
                          </span>
                          <span
                            className={
                              getPOLineItemAmount(
                                poItems[lineItem.id] || [],
                                "taxPercentage"
                              ) > getEffectiveAvailableAmount(lineItem)
                                ? "error"
                                : ""
                            }
                          >
                            Order Amount :{" "}
                            {getPOLineItemAmount(
                              poItems[lineItem.id] || [],
                              "taxPercentage"
                            )}
                          </span>
                        </div>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails>
                      {(poItems[lineItem.id] || []).map((poItem, index) => {
                        // Falls back to a lineItemId+itemId match when prItemId can't resolve
                        // (e.g. a PO saved before the prItemId-preserving fixes existed).
                        const prRemaining = getPrRemainingForPoItem(poItem);
                        // An editable PO (draft / reconsider) holds no PR claim -- draft never
                        // claimed, reconsider/reject released it (releasePRConversion) -- so the
                        // PR's live balance already includes this PO's own quantity. Adding
                        // poItem.quantity on top double-counted it (PR qty 1 showed as 2), and the
                        // backend's update/resend check uses the live balance alone.
                        const prQuantity = prRemaining;
                        const rateContractMissing =
                          !!poItem?.item?.rateContractRequired &&
                          !rateContractStatusMap[poItem.itemId]?.active;
                        return (
                          <AddItemModal
                            poItem={poItem}
                            lineItem={lineItem}
                            vendorCode={
                              formDetails.vendor && formDetails.vendor.code
                            }
                            isItemDeleted={config.isItemDeleted}
                            updateItem={(item) => updateItem(item, index)}
                            onDelete={() => onDeleteRow(lineItem.id, index)}
                            prQuantity={prQuantity}
                            isPRLinked={isPRLinked}
                            poDate={formDetails.poDate}
                            rateContractMissing={rateContractMissing}
                          />
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
              <ExtraChargesSection
                charges={extraCharges}
                onChangeCharges={setExtraCharges}
                poSubtotalBeforeTax={poSubtotalBeforeTax}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="po-attachment-form-container">
          <span>Please select vendor to add items</span>
        </div>
      )}
    </div>
  );
};

export default EditPO;
